import { match } from 'ts-pattern';
import type { FilterConfigItemBasic } from '../../../../../../lib/api/filterConfig';
import {
  AgeRangeComponent,
  CareTeamComponent,
  ChannelRecencyComponent,
  ClaimsLookbackPeriodComponent,
  ConceptDescriptorComponent,
  ContextualLookbackPeriodComponent,
  DiagnosisDecileComponent,
  DiagnosisNameComponent,
  DomainsSliderComponent,
  EmailComponent,
  FacilityAffiliationComponent,
  FacilityNameComponent,
  GenderComponent,
  GraduationYearComponent,
  IABTaxonomyComponent,
  InsuranceProviderComponent,
  InsuranceTypeComponent,
  MatchTypeComponent,
  MediaComponent,
  MedicalSchoolComponent,
  MeshTaxonomyComponent,
  NetWorthComponent,
  NPIListNameComponent,
  NPIMarketAreaComponent,
  NPIZipcodeComponent,
  NumberOfPatientsComponent,
  PatientAgeRangeComponent,
  PatientFacilityComponent,
  PatientGenderComponent,
  PatientStateComponent,
  PrescribedDrugDecileComponent,
  PrescribedDrugNameComponent,
  PrescribingBehaviorChangeComponent,
  ProcedureDecileComponent,
  ProcedureNameComponent,
  ProfessionComponent,
  SearchComponent,
  SiteComponent,
  SocialComponent,
  StateNameComponent,
  TagCollectionComponent,
  TopicDescriptorComponent,
  YearsPracticedRangeComponent,
} from '../FilterConfigCustomComponents';
import type { FilterConfigItem } from '../FilterConfigTypes';

const extendFilter = (filter: FilterConfigItemBasic) => {
  const customComponent = match(filter)
    .with({ filterName: 'patient_age_range_filter' }, () => PatientAgeRangeComponent)
    .with({ filterName: 'patient_insurance_provider_name_filter' }, () => ({
      customComponent: InsuranceProviderComponent,
      enableBulkUpload: true,
    }))
    .with({ filterName: 'patient_insurance_type_filter' }, () => InsuranceTypeComponent)
    .with({ filterName: 'patient_state_name_filter' }, () => ({
      customComponent: PatientStateComponent,
      enableBulkUpload: true,
    }))
    .with({ filterName: 'patient_facility_name_filter' }, () => ({
      customComponent: PatientFacilityComponent,
      enableBulkUpload: true,
      vectorField: 'facility',
    }))
    .with({ filterName: 'age_range_filter' }, () => AgeRangeComponent)
    .with({ filterName: 'years_practiced_range_filter' }, () => YearsPracticedRangeComponent)
    .with({ filterName: 'net_worth_filter' }, () => NetWorthComponent)
    .with({ filterName: 'state_name_filter' }, () => ({ customComponent: StateNameComponent, enableBulkUpload: true }))
    .with({ filterName: 'medical_school_filter' }, () => ({
      customComponent: MedicalSchoolComponent,
      vectorField: 'medical_school',
    }))
    .with({ filterName: 'graduation_year_filter' }, () => GraduationYearComponent)
    .with({ filterName: 'patient_gender_filter' }, () => PatientGenderComponent)
    .with({ filterName: 'number_of_patients_range_filter' }, () => NumberOfPatientsComponent)
    .with({ filterName: 'procedure_name_filter' }, () => ({
      customComponent: ProcedureNameComponent,
      enableBulkUpload: true,
      useApiSuggestions: true,
      vectorField: 'procedure',
    }))
    .with({ filterName: 'procedure_decile_filter' }, () => ProcedureDecileComponent)
    .with({ filterName: 'diagnosis_name_filter' }, () => ({
      customComponent: DiagnosisNameComponent,
      enableBulkUpload: true,
      useApiSuggestions: true,
      vectorField: 'diagnosis',
    }))
    .with({ filterName: 'diagnosis_decile_filter' }, () => DiagnosisDecileComponent)
    .with({ filterName: 'prescribed_drug_name_filter' }, () => ({
      customComponent: PrescribedDrugNameComponent,
      enableBulkUpload: true,
      useApiSuggestions: true,
      vectorField: 'drug',
    }))
    .with({ filterName: 'prescribed_drug_decile_filter' }, () => PrescribedDrugDecileComponent)
    .with({ filterName: 'prescribing_behavior_change_filter' }, () => PrescribingBehaviorChangeComponent)
    .with({ filterName: 'prescribing_behavior_drug_name_filter' }, () => ({
      customComponent: PrescribingBehaviorChangeComponent,
      enableBulkUpload: true,
      useApiSuggestions: true,
      vectorField: 'drug',
    }))
    .with({ filterName: 'prescribing_behavior_decile_filter' }, () => PrescribingBehaviorChangeComponent)
    .with({ filterName: 'prescribing_behavior_prescription_type_filter' }, () => PrescribingBehaviorChangeComponent)
    .with({ filterName: 'extend_with_facility_affiliation' }, () => FacilityAffiliationComponent)
    .with({ filterName: 'extend_with_care_team' }, () => CareTeamComponent)
    .with({ filterName: 'contextual_lookback_period_filter' }, () => ContextualLookbackPeriodComponent)
    .with({ filterName: 'gender_filter' }, () => GenderComponent)
    .with({ filterName: 'contextual_iab_taxonomy_id_filter' }, () => IABTaxonomyComponent)
    .with({ filterName: 'contextual_iab_decile_filter' }, () => IABTaxonomyComponent)
    .with({ filterName: 'contextual_mesh_taxonomy_id_filter' }, () => MeshTaxonomyComponent)
    .with({ filterName: 'contextual_mesh_decile_filter' }, () => MeshTaxonomyComponent)
    .with({ filterName: 'contextual_topic_descriptor_filter' }, () => ({
      customComponent: TopicDescriptorComponent,
      vectorField: 'topic_descriptor',
    }))
    .with({ filterName: 'contextual_concept_descriptor_filter' }, () => ({
      customComponent: ConceptDescriptorComponent,
      vectorField: 'concept_descriptor',
    }))
    .with({ filterName: 'npi_dma_filter' }, () => ({ customComponent: NPIMarketAreaComponent, enableBulkUpload: true }))
    .with({ filterName: 'npi_list_name_filter' }, () => NPIListNameComponent)
    .with({ filterName: 'facility_name_filter' }, () => ({
      customComponent: FacilityNameComponent,
      enableBulkUpload: true,
      vectorField: 'facility',
    }))
    .with({ filterName: 'npi_zipcode_filter' }, () => ({
      customComponent: NPIZipcodeComponent,
      enableBulkUpload: true,
    }))
    .with({ filterName: 'profession_filter' }, () => ({
      customComponent: ProfessionComponent,
      enableBulkUpload: true,
      vectorField: 'profession',
    }))
    .with({ filterName: 'specialty_filter' }, () => ({
      customComponent: ProfessionComponent,
      enableBulkUpload: true,
      vectorField: 'specialty',
    }))
    .with({ filterName: 'primary_specialty_filter' }, () => ProfessionComponent)
    .with({ filterName: 'secondary_specialty_filter' }, () => ProfessionComponent)
    .with({ filterName: 'tertiary_specialty_filter' }, () => ProfessionComponent)
    .with({ filterName: 'email_campaign_filter' }, () => EmailComponent)
    .with({ filterName: 'email_collection_filter' }, () => EmailComponent)
    .with({ filterName: 'email_action_filter' }, () => EmailComponent)
    .with({ filterName: 'media_campaign_filter' }, () => MediaComponent)
    .with({ filterName: 'media_events_filter' }, () => MediaComponent)
    .with({ filterName: 'search_events_filter' }, () => SearchComponent)
    .with({ filterName: 'site_events_filter' }, () => SiteComponent)
    .with({ filterName: 'social_events_filter' }, () => SocialComponent)
    .with({ filterName: 'email_events_filter' }, () => EmailComponent)
    .with({ filterName: 'media_collection_filter' }, () => MediaComponent)
    .with({ filterName: 'search_ad_group_filter' }, () => SearchComponent)
    .with({ filterName: 'search_campaign_filter' }, () => SearchComponent)
    .with({ filterName: 'search_collection_filter' }, () => SearchComponent)
    .with({ filterName: 'search_keyword_filter' }, () => SearchComponent)
    .with({ filterName: 'search_param1_filter' }, () => SearchComponent)
    .with({ filterName: 'search_param2_filter' }, () => SearchComponent)
    .with({ filterName: 'search_param3_filter' }, () => SearchComponent)
    .with({ filterName: 'search_param4_filter' }, () => SearchComponent)
    .with({ filterName: 'search_param5_filter' }, () => SearchComponent)
    .with({ filterName: 'site_domains_filter' }, () => DomainsSliderComponent)
    .with({ filterName: 'smart_pixel_lookback_period_filter' }, () => ChannelRecencyComponent)
    .with({ filterName: 'claims_lookback_period_filter' }, () => ClaimsLookbackPeriodComponent)
    .with({ filterName: 'media_action_filter' }, () => MediaComponent)
    .with({ filterName: 'media_frequency_filter' }, () => MediaComponent)
    .with({ filterName: 'smart_pixel_collection_filter' }, () => TagCollectionComponent)
    .with({ filterName: 'social_campaign_filter' }, () => SocialComponent)
    .with({ filterName: 'site_page_url_filter' }, () => SiteComponent)
    .with({ filterName: 'site_pageviews_filter' }, () => SiteComponent)
    .with({ filterName: 'site_param1_filter' }, () => SiteComponent)
    .with({ filterName: 'site_param2_filter' }, () => SiteComponent)
    .with({ filterName: 'site_param3_filter' }, () => SiteComponent)
    .with({ filterName: 'site_param4_filter' }, () => SiteComponent)
    .with({ filterName: 'site_param5_filter' }, () => SiteComponent)
    .with({ filterName: 'site_formtext_filter' }, () => SiteComponent)
    .with({ filterName: 'site_clicktext_filter' }, () => SiteComponent)
    .with({ filterName: 'smart_pixel_match_type_filter' }, () => MatchTypeComponent)
    .otherwise(() => null);

  if (!customComponent) return filter as FilterConfigItem;
  const extension =
    typeof customComponent === 'object' && 'customComponent' in customComponent ? customComponent : { customComponent };
  return { ...filter, ...extension } as FilterConfigItem;
};

export default extendFilter;
